import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, MousePointer, Users, ImageIcon, TrendingUp, BarChart2, ArrowLeft } from "lucide-react";
import useDashboard from "../hooks/useDashboard";

// ── CountUp animation ─────────────────────────────────────────────────────────
function useCountUp(target) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const end = Number(target) || 0;
    if (end === 0) { setVal(0); return; }
    let cur = 0;
    const steps = 36;
    const inc = end / steps;
    const tid = setInterval(() => {
      cur = Math.min(cur + inc, end);
      setVal(Math.round(cur));
      if (cur >= end) clearInterval(tid);
    }, 700 / steps);
    return () => clearInterval(tid);
  }, [target]);
  return val;
}

// ── Card config ───────────────────────────────────────────────────────────────
const CARDS = [
  {
    label: "Post Views",
    key:   "total_views",
    Icon:  Eye,
    grad:  "from-blue-500/20 via-blue-500/10 to-transparent",
    border:"border-blue-500/25",
    icon:  "bg-blue-500/20 text-blue-400",
    value: "text-blue-300",
    glow:  "shadow-blue-500/10",
  },
  {
    label: "Link Clicks",
    key:   "total_clicks",
    Icon:  MousePointer,
    grad:  "from-emerald-500/20 via-emerald-500/10 to-transparent",
    border:"border-emerald-500/25",
    icon:  "bg-emerald-500/20 text-emerald-400",
    value: "text-emerald-300",
    glow:  "shadow-emerald-500/10",
  },
  {
    label: "Followers",
    key:   "followers",
    Icon:  Users,
    grad:  "from-purple-500/20 via-purple-500/10 to-transparent",
    border:"border-purple-500/25",
    icon:  "bg-purple-500/20 text-purple-400",
    value: "text-purple-300",
    glow:  "shadow-purple-500/10",
  },
  {
    label: "Posts",
    key:   "post_count",
    Icon:  ImageIcon,
    grad:  "from-orange-500/20 via-orange-500/10 to-transparent",
    border:"border-orange-500/25",
    icon:  "bg-orange-500/20 text-orange-400",
    value: "text-orange-300",
    glow:  "shadow-orange-500/10",
  },
];

// ── Summary card ──────────────────────────────────────────────────────────────
function SummaryCard({ cfg, rawValue }) {
  const display = useCountUp(rawValue);
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${cfg.border}
      bg-gradient-to-br ${cfg.grad} shadow-lg ${cfg.glow} p-4`}>
      <div className="flex items-start justify-between mb-3">
        <span className="text-white/45 text-[11px] uppercase tracking-wider font-medium">
          {cfg.label}
        </span>
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${cfg.icon}`}>
          <cfg.Icon className="w-4 h-4" />
        </div>
      </div>
      <p className={`font-bold text-[28px] leading-none tabular-nums ${cfg.value}`}>
        {display.toLocaleString()}
      </p>
    </div>
  );
}

// ── Colored timeseries chart ──────────────────────────────────────────────────
function TimeseriesChart({ data }) {
  if (!data || data.length < 2) {
    return (
      <div className="h-36 flex items-center justify-center text-white/20 text-sm">
        Not enough data yet
      </div>
    );
  }

  const W = 600, H = 140;
  const PAD = { t: 10, r: 12, b: 30, l: 12 };
  const cW = W - PAD.l - PAD.r;
  const cH = H - PAD.t - PAD.b;

  const maxVal = Math.max(...data.map(d => Math.max(d.views, d.clicks)), 1);
  const x = i  => PAD.l + (i / (data.length - 1)) * cW;
  const y = v  => PAD.t + cH - (v / maxVal) * cH;
  const toPath = key =>
    data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(' ');

  const viewsPath  = toPath('views');
  const clicksPath = toPath('clicks');

  // Area fill path (close at bottom)
  const areaViews =
    viewsPath +
    ` L${x(data.length - 1).toFixed(1)},${(PAD.t + cH).toFixed(1)}` +
    ` L${PAD.l},${(PAD.t + cH).toFixed(1)} Z`;

  const labelIdxs = [...new Set(
    [0, 6, 13, 20, 27, data.length - 1].filter(i => i < data.length)
  )];

  return (
    <div>
      <div className="flex gap-5 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-400" />
          <span className="text-[11px] text-white/45">Views</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-400" />
          <span className="text-[11px] text-white/45">Clicks</span>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 140 }}>
        <defs>
          <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#60a5fa" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {/* Grid */}
        {[0.25, 0.5, 0.75, 1].map(p => (
          <line key={p}
            x1={PAD.l} y1={PAD.t + cH * (1 - p)}
            x2={W - PAD.r} y2={PAD.t + cH * (1 - p)}
            stroke="rgba(255,255,255,0.06)" strokeWidth="1"
          />
        ))}

        {/* Views area fill */}
        <path d={areaViews} fill="url(#viewsGrad)" />

        {/* Views line */}
        <path d={viewsPath} fill="none"
          stroke="#60a5fa" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />

        {/* Clicks line */}
        <path d={clicksPath} fill="none"
          stroke="#34d399" strokeWidth="2"
          strokeDasharray="5 4" strokeLinecap="round" strokeLinejoin="round" />

        {/* X labels */}
        {labelIdxs.map(i => (
          <text key={i} x={x(i)} y={H - 4} textAnchor="middle"
            fontSize="9" fill="rgba(255,255,255,0.25)">
            {data[i].date.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

// ── Category color map ────────────────────────────────────────────────────────
const CAT_COLORS = {
  fashion: "bg-pink-500/20 text-pink-400",
  beauty:  "bg-rose-500/20 text-rose-400",
  watches: "bg-yellow-500/20 text-yellow-400",
  perfume: "bg-violet-500/20 text-violet-400",
  home:    "bg-teal-500/20 text-teal-400",
  food:    "bg-orange-500/20 text-orange-400",
  cars:    "bg-sky-500/20 text-sky-400",
  other:   "bg-white/10 text-white/40",
};

// ── Post row ──────────────────────────────────────────────────────────────────
function PostRow({ post, onClick }) {
  const catStyle = CAT_COLORS[post.category] || CAT_COLORS.other;
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-3 px-3 py-3 rounded-xl
        hover:bg-white/[0.04] active:bg-white/[0.07] cursor-pointer transition-colors group"
    >
      <div className="w-11 h-11 rounded-xl overflow-hidden bg-white/[0.06] shrink-0
        ring-1 ring-white/5 group-hover:ring-white/15 transition-all">
        {post.image
          ? <img src={post.image} alt="" className="w-full h-full object-cover"
              onError={e => { e.target.style.display = 'none'; }} />
          : <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-4 h-4 text-white/20" />
            </div>
        }
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-white/80 text-sm truncate leading-tight font-medium">
          {post.caption || 'Untitled post'}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium ${catStyle}`}>
            {post.category}
          </span>
          <span className="text-[10px] text-white/25">{post.created_at}</span>
        </div>
      </div>

      <div className="flex gap-3 shrink-0">
        {[
          ['views',  post.views,  'text-blue-400'],
          ['clicks', post.clicks, 'text-emerald-400'],
          ['saves',  post.saves,  'text-purple-400'],
        ].map(([label, val, color]) => (
          <div key={label} className="text-center min-w-[30px]">
            <p className={`text-sm font-bold tabular-nums leading-none ${color}`}>{val}</p>
            <p className="text-white/25 text-[9px] mt-0.5">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton({ className = '' }) {
  return <div className={`bg-white/[0.05] animate-pulse rounded-2xl ${className}`} />;
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-1 h-4 rounded-full bg-gradient-to-b from-white/60 to-white/20" />
      <p className="text-white/50 text-xs uppercase tracking-wider font-semibold">{children}</p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const navigate = useNavigate();
  const {
    summary, posts, links, timeseries,
    page, setPage, totalPages,
    sort, setSort,
    loading, postsLoading, error,
  } = useDashboard();

  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-3">
        <BarChart2 className="w-8 h-8 text-white/20" />
        <p className="text-white/40 text-sm text-center px-8">{error}</p>
        <button onClick={() => window.location.reload()}
          className="text-white/30 hover:text-white text-xs underline transition">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white px-4 pt-5 pb-24">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)}
          className="p-2 rounded-xl hover:bg-white/8 transition text-white/40 hover:text-white">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
            Analytics
          </h1>
          <p className="text-white/30 text-xs mt-0.5">Your content performance</p>
        </div>
      </div>

      {/* ── Summary cards ── */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {CARDS.map(cfg => (
            <SummaryCard key={cfg.key} cfg={cfg} rawValue={summary?.[cfg.key]} />
          ))}
        </div>
      )}

      {/* ── 30-day chart ── */}
      <div className="mb-8">
        <SectionLabel>Last 30 Days</SectionLabel>
        <div className="bg-gradient-to-b from-white/[0.04] to-transparent
          border border-white/[0.08] rounded-2xl p-4">
          {loading
            ? <Skeleton className="h-36" />
            : <TimeseriesChart data={timeseries} />
          }
        </div>
      </div>

      {/* ── Your posts ── */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>Your Posts</SectionLabel>
          <div className="flex gap-1.5">
            {[
              ['recent', 'Recent', 'text-white'],
              ['views',  'Views',  'text-blue-400'],
              ['clicks', 'Clicks', 'text-emerald-400'],
            ].map(([v, label, activeText]) => (
              <button
                key={v}
                onClick={() => { setSort(v); setPage(1); }}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all
                  ${sort === v
                    ? `bg-white/15 ${activeText} border border-white/20`
                    : 'bg-white/[0.05] text-white/35 hover:bg-white/[0.10]'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white/[0.02] border border-white/[0.07] rounded-2xl overflow-hidden">
          {postsLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : posts.length === 0 ? (
            <div className="py-14 flex flex-col items-center gap-3">
              <ImageIcon className="w-8 h-8 text-white/15" />
              <p className="text-white/30 text-sm">No posts yet. Create your first!</p>
              <button onClick={() => navigate('/home')}
                className="mt-1 px-5 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500
                  text-white text-sm font-medium hover:opacity-90 transition">
                Go to Feed
              </button>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.04] p-2">
              {posts.map(post => (
                <PostRow
                  key={post.post_id}
                  post={post}
                  onClick={() => navigate(`/post/${post.post_id}`)}
                />
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && !postsLoading && (
          <div className="flex items-center justify-center gap-3 mt-4">
            <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}
              className="px-4 py-1.5 rounded-xl text-xs text-white/50
                bg-white/[0.06] disabled:opacity-25 hover:bg-white/10 transition border border-white/10">
              ← Prev
            </button>
            <span className="text-xs text-white/30 tabular-nums">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}
              className="px-4 py-1.5 rounded-xl text-xs text-white/50
                bg-white/[0.06] disabled:opacity-25 hover:bg-white/10 transition border border-white/10">
              Next →
            </button>
          </div>
        )}
      </div>

      {/* ── Top links ── */}
      <div>
        <SectionLabel>Top Links</SectionLabel>
        <div className="bg-white/[0.02] border border-white/[0.07] rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : links.length === 0 ? (
            <div className="py-10 flex flex-col items-center gap-2">
              <TrendingUp className="w-7 h-7 text-white/15" />
              <p className="text-white/30 text-sm">No links yet</p>
              <p className="text-white/20 text-xs">Add links to your posts to track clicks</p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.04]">
              {links.slice(0, 5).map((link, i) => {
                const rankColors = ['text-yellow-400', 'text-gray-300', 'text-orange-400'];
                const rankColor  = rankColors[i] || 'text-white/25';
                return (
                  <div key={link.link_id} className="flex items-center gap-3 px-4 py-3
                    hover:bg-white/[0.03] transition-colors">
                    <span className={`text-sm font-bold w-5 text-center tabular-nums shrink-0 ${rankColor}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/75 text-sm truncate font-medium">{link.label}</p>
                      <p className="text-white/25 text-xs truncate font-mono">{link.url}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-emerald-400 text-sm font-bold tabular-nums">{link.clicks}</p>
                      <p className="text-white/25 text-[10px]">clicks</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
